import type { SbBlokData } from "@storyblok/react";
import { useWithdrawal, Withdrawal } from "@trydig/withdrawal/react";
import { useRouter } from "next/router";

import { declareWithdrawalAction, lookupOrderAction } from "./actions";
import styles from "./RightOfWithdrawal.module.scss";

export interface BlokRightOfWithdrawal extends SbBlokData {
  header?: string;
  emailLabel?: string;
  orderNumberLabel?: string;
  continueButton?: string;
  confirmButton?: string;
  withdrawedItemsText?: string;
  successHeader?: string;
  successText?: string;
  confirmationText?: string;
  errorNotFound?: string;
  errorExpired?: string;
  error?: string;
}

const ErrorBox = ({ blok }: { blok: BlokRightOfWithdrawal }) => {
  const { phase } = useWithdrawal();

  return (
    <>
      {phase === "not_found" && (
        <p className={styles.error}>{blok.errorNotFound}</p>
      )}
      {phase === "expired" && (
        <p className={styles.error}>{blok.errorExpired}</p>
      )}
      {phase === "error" && <p className={styles.error}>{blok.error}</p>}
    </>
  );
};

const WithdrawalSummary = ({ blok }: { blok: BlokRightOfWithdrawal }) => {
  const { order, declared, email } = useWithdrawal();

  if (!order) return null;

  return (
    <>
      {declared ? (
        <Withdrawal.Success>
          <div className={styles.success}>
            <p className={styles.successTitle}>{blok.successHeader}</p>
            <p className={styles.successText}>{blok.successText}</p>
            <dl className={styles.successMeta}>
              <div>
                <dt>{blok.orderNumberLabel}</dt>
                <dd>#{order.orderNumber}</dd>
              </div>
              <div>
                <dt>{blok.confirmButton}</dt>
                <dd>{email}</dd>
              </div>
            </dl>
          </div>
        </Withdrawal.Success>
      ) : (
        <div className={styles.summary}>
          <Withdrawal.Items className={styles.items} />

          {order.items.some((i) => (i.claimed ?? 0) > 0) && (
            <div className={styles.notice}>
              <p>{blok.withdrawedItemsText}</p>
            </div>
          )}

          <div className={styles.notice}>
            <p>
              {blok.confirmationText} {email}
            </p>
          </div>

          <Withdrawal.ConfirmButton className={styles.confirmButton}>
            {blok.confirmButton}
          </Withdrawal.ConfirmButton>
        </div>
      )}
    </>
  );
};

export const RightOfWithdrawal = ({
  blok,
}: {
  blok: BlokRightOfWithdrawal;
}) => {
  const router = useRouter();
  const locale = router.locale ?? "no";

  return (
    <Withdrawal.Root
      actions={{
        lookupOrder: lookupOrderAction,
        declareWithdrawal: declareWithdrawalAction,
      }}
      locale={locale}
    >
      <div className={styles.root}>
        <div className={styles.inner}>
          <h2 className={styles.title}>{blok.header}</h2>

          <Withdrawal.Lookup>
            <div className={styles.form}>
              <Withdrawal.Field name="email" label={blok.emailLabel} />
              <Withdrawal.Field
                name="orderNumber"
                label={blok.orderNumberLabel}
              />

              <ErrorBox blok={blok} />

              <Withdrawal.LookupButton className={styles.lookupButton}>
                {blok.continueButton}
              </Withdrawal.LookupButton>
            </div>
          </Withdrawal.Lookup>

          <WithdrawalSummary blok={blok} />
        </div>
      </div>
    </Withdrawal.Root>
  );
};
